import { Flex, Button, Icon, Spinner } from '../../styles';
import { IconPlay } from '../../icons';
import { getTimerDuration, usePlayerService } from '../../utils';
import { __t } from '../../i18n';
import CountdownTimer from '../../CountdownTimer';
import React, { ComponentProps, useEffect, useState } from 'react';
import useConditionalProps from '../../utils/useConditionalProps';
import dynamic from 'next/dynamic';
import { getPlayLabel } from '../../utils/usePlayerService';
import lscache from 'lscache';

const Reaction = dynamic(() => import('../../Reaction'), { ssr: false });

const ContentActions = ({ hasTrailer, countdown_date, carrierBtnText, comingSoonFlag }: ContentActionsProps) => {
  const loading = useConditionalProps((s) => s.loading);
  const contentId = useConditionalProps((s) => s.contentId);
  const free = useConditionalProps((s) => s.free);
  const reaction = useConditionalProps((s) => s.reaction);
  const serverTime = useConditionalProps((s) => s.serverTime);
  const contentLongId = useConditionalProps((s) => s.contentLongId);
  const fetchConditionalProps = useConditionalProps((s) => s.fetch);
  const trailerId = contentId;
  const [watchTrailer, { loading: loadingPlay }] = usePlayerService({ id: trailerId });
  const [timer, setTimer] = useState<number>();
  const [released, setReleased] = useState(false);
  useEffect(() => {
    setTimer(serverTime ? getTimerDuration(countdown_date, serverTime) : undefined);
    setReleased(false);
  }, [countdown_date, serverTime]);

  // The countdown runs to the release date on the server's clock, so its end is the release: the
  // content is out from here on, and the viewer's conditional props are fetched again for its CTA.
  const handleCountdownEnd = () => {
    setTimer(undefined);
    setReleased(true);
    fetchConditionalProps();
  };
  const comingSoon = comingSoonFlag && !released;

  return (
    <>
      <Flex direction={{ xs: 'column', sm: 'row' }} align={{ xs: 'flex-start', sm: 'normal' }} mt={{ xs: 1, sm: 3 }}>
        {!!timer && comingSoon && (
          <CountdownTimer value={timer} onFinishTimer={handleCountdownEnd} ml={{ sm: 2 }} mb={{ xs: 3, sm: 0 }} />
        )}
        <Flex mb={{ xs: 3, sm: 0 }}>
          <CTA loading={loading} free={free} countdown={timer} carrierBtnText={carrierBtnText} comingSoonFlag={comingSoon} />
          {hasTrailer && <TrailerButton onClick={() => watchTrailer('t')} disabled={loadingPlay} />}
        </Flex>
        {!comingSoon && <Flex align="center">
          <Reaction type="content" flag={reaction} id={contentId || contentLongId as string} loading={loading} round />
        </Flex>
        }
      </Flex>
    </>
  );
};

const CTA = ({ loading: loadingProp, free, countdown, carrierBtnText, comingSoonFlag }: CTAProps) => {
  const contentId = useConditionalProps((s) => s.contentId);
  const firstEpisodeId = useConditionalProps((s) => s.firstEpisodeId);
  const ticket = useConditionalProps((s) => s.ticket);
  const price_button_text = useConditionalProps((s) => s.ticket?.price_button_text);
  const playable = useConditionalProps((s) => s.playable);
  const showtime = useConditionalProps((s) => s.showtime);
  const hasAccess = useConditionalProps((s) => s.hasAccess);
  const hasSpecificCarrie = useConditionalProps((s) => s.hasSpecificCarrie);
  const [isTapsiSSO, setIsTapsiSSO] = useState<boolean>(false);
  const id = firstEpisodeId || contentId;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsTapsiSSO(lscache.get('utm_source_sso') === 'tapsi');
    }
  }, []);

  const [handleWatch, { loading: loadingPrimary, action }] = usePlayerService({
    id, free, ticket,
    showtime, playable, hasAccess, hasSpecificCarrie, isTapsiSSO,
  });
  const loading = loadingPrimary || loadingProp;

  if (loading || !action || (!playable && action !== 'purchase') || (countdown && comingSoonFlag)) return null;

  const label = getPlayLabel(action, ticket?.price, price_button_text, carrierBtnText);
  const variant = action === 'purchase' ? 'primaryB' : 'primaryA';
  const showIcon = action === 'watchAccess' || action === 'carrierWatch';

  const handleClick = () => {
    if (!comingSoonFlag) handleWatch('p');
  };

  return (
    <Button variant={variant} iconStart={showIcon || loading} disabled={loading} onClick={handleClick} ml={2}>
      {comingSoonFlag ? __t('common.soon') : <>
        <CTAIcon loading={loading} showIcon={showIcon} />
        {__t(...label)}
      </>}
    </Button>
  );
};

const CTAIcon = ({ loading, showIcon }: { loading: boolean; showIcon: boolean }) => {
  if (loading) return <Spinner />;
  if (!showIcon) return null;
  return (
    <Icon cursor="pointer">
      <IconPlay />
    </Icon>
  );
};

const TrailerButton = ({
  onClick: handleClick,
  disabled,
}: Pick<ComponentProps<typeof Button>, 'disabled' | 'onClick'>) => {
  return (
    <Button variant="secondary" onClick={handleClick} disabled={disabled} ml={2}>
      {__t('content.preview')}
    </Button>
  );
};

type ContentActionsProps = {
  hasTrailer: boolean;
  countdown_date?: string;
  carrierBtnText?: string;
  comingSoonFlag?: boolean;
};

type CTAProps = {
  loading: boolean;
  countdown?: number;
  free: boolean;
  carrierBtnText?: string;
  comingSoonFlag?: boolean;
} & Pick<ComponentProps<typeof Button>, 'disabled'>;

export default ContentActions;
